import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { getBrandingForDisplay, updateBranding, resetBranding, uploadBrandingLogo, uploadBrandingLogoMini, uploadBrandingFavicon } from 'services/company-service';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';
import { Shimmer } from 'Components/PageLoader/styles';
import { Main as Container } from 'Components/Main';
import styled from 'styled-components';
import { Dialog, TextField, PageHeader } from 'Components/ui';

// ─── SVG icons ────────────────────────────────────────────────────────────────

const LockSvg = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const CheckSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--vo-celeste)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ImageSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);

const SquareSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="5" width="14" height="14" rx="2" ry="2"/>
  </svg>
);

const MonitorSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
    <line x1="8" y1="21" x2="16" y2="21"/>
    <line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
);

const PaletteSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 8v8M8 12h8"/>
  </svg>
);

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrap = styled.div`
  padding-top: 16px;
  padding-bottom: 40px;
`;

// ── Locked state ──────────────────────────────────────────────────────────────

const LockedCard = styled.div`
  max-width: 560px;
  background: white;
  border-radius: 14px;
  padding: 40px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  text-align: center;
`;

const LockBadge = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
`;

const LockedTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: var(--vo-ink);
  margin: 0 0 8px;
  letter-spacing: -0.01em;
`;

const LockedDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-slate);
  margin: 0 0 24px;
  line-height: 1.65;

  strong { color: var(--vo-cobalto); }
`;

const FeatureChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-bottom: 28px;
`;

const FeatureChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: var(--vo-mist);
  border-radius: 10px;
`;

const ChipText = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-ink-2);
`;

const UpgradeBtn = styled.button`
  height: 48px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover { background: var(--vo-cobalto); }
`;

// ── Unlocked state ────────────────────────────────────────────────────────────

const CardsCol = styled.div`
  max-width: 680px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--vo-cloud);
`;

const IconBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SectionTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;

const SectionSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

// Logo / mini preview box on dark navy bg
const NavyPreview = styled.div`
  background: var(--vo-escuro);
  border-radius: 10px;
  padding: ${({ $wide }) => $wide ? '16px 24px' : '16px'};
  margin-bottom: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ $square }) => $square && 'width: 64px;'}
`;

// Favicon preview box
const FaviconPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const FaviconBox = styled.div`
  width: 32px;
  height: 32px;
  border: 1px solid var(--vo-cloud);
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FaviconName = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`;

const OutlineBtn = styled.button`
  height: 40px;
  background: white;
  color: var(--vo-celeste);
  border: 1.5px solid var(--vo-celeste);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover { background: var(--vo-capri-50); }
`;

// Color section
const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const ColorFieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ColorLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-ink-2);
`;

const ColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ColorSwatch = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${({ $color }) => $color || 'var(--vo-celeste)'};
  border: 2px solid var(--vo-line);
  cursor: pointer;
  flex-shrink: 0;
`;

const HiddenColorInput = styled.input.attrs({ type: 'color' })`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
`;

// Bespoke — matches the swatch's 44px height for row alignment; not
// CLOSED_SX (that's 40px). size="sm" opts out of CLOSED_SX for a clean slate.
const COLOR_TEXT_SX = {
  flex: 1,
  '& .MuiOutlinedInput-root': {
    height: '44px',
    borderRadius: '10px',
  },
  '& .MuiOutlinedInput-input': {
    height: '44px',
    boxSizing: 'border-box',
    padding: '0 14px',
    fontFamily: "'Lato', monospace, sans-serif",
    fontSize: '14px',
    color: 'var(--vo-ink)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--vo-steel)',
    borderWidth: '1.5px',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--vo-steel)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--vo-celeste)',
    borderWidth: '1.5px',
  },
};

// Action area
const Divider = styled.div`
  height: 1px;
  background: var(--vo-cloud);
`;

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
`;

const PrimaryBtn = styled.button`
  height: 48px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover { background: var(--vo-cobalto); }
  &:disabled { background: var(--vo-steel); cursor: not-allowed; }
`;

const SecondaryBtn = styled.button`
  height: 48px;
  background: white;
  color: var(--vo-slate);
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  padding: 0 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover { background: var(--vo-mist); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ShimmerStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 680px;
`;

// ─── ELIGIBLE_PLANS ────────────────────────────────────────────────────────────

const ELIGIBLE_PLANS = ['premium'];

// ─── Component ────────────────────────────────────────────────────────────────

export const BrandingSettings = () => {
  const { t } = useTranslation(['company']);
  const queryClient = useQueryClient();
  const tenant = useSelector(useAuthTenant);
  const isEligible = ELIGIBLE_PLANS.includes(tenant?.signedPlan?.toLowerCase() ?? '');
  const targetSlug = tenant?.slug || null;

  const [primaryColor, setPrimaryColor] = useState('var(--vo-celeste)');
  const [secondaryColor, setSecondaryColor] = useState('var(--vo-capri)');
  const [logoFile, setLogoFile] = useState(null);
  const [logoMiniFile, setLogoMiniFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoMiniPreview, setLogoMiniPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);

  const logoInputRef = useRef();
  const logoMiniInputRef = useRef();
  const faviconInputRef = useRef();
  const primaryPickerRef = useRef();
  const secondaryPickerRef = useRef();

  const { isLoading, data: brandingData } = useQuery(
    ['company-branding', tenant?.slug],
    getBrandingForDisplay,
    { staleTime: 5 * 60 * 1000, enabled: isEligible }
  );

  useEffect(() => {
    const b = brandingData?.data?.branding;
    if (b === undefined) return;
    setPrimaryColor(b?.primaryColor || 'var(--vo-celeste)');
    setSecondaryColor(b?.secondaryColor || 'var(--vo-capri)');
    setLogoPreview(b?.logoUrl || null);
    setLogoMiniPreview(b?.logoMiniUrl || null);
    setFaviconPreview(b?.faviconUrl || null);
  }, [brandingData]);

  const saveMutation = useMutation(
    async () => {
      if (logoFile) await uploadBrandingLogo(logoFile, targetSlug);
      if (logoMiniFile) await uploadBrandingLogoMini(logoMiniFile, targetSlug);
      if (faviconFile) await uploadBrandingFavicon(faviconFile, targetSlug);
      await updateBranding({ primaryColor, secondaryColor }, targetSlug);
    },
    {
      onSuccess: () => {
        const root = document.documentElement;
        if (primaryColor) root.style.setProperty('--color-primary', primaryColor);
        else root.style.removeProperty('--color-primary');
        if (secondaryColor) root.style.setProperty('--color-secondary', secondaryColor);
        else root.style.removeProperty('--color-secondary');
        setLogoFile(null);
        setLogoMiniFile(null);
        setFaviconFile(null);
        queryClient.invalidateQueries(['company-branding', tenant?.slug]);
        Dialog.fire({ icon: 'success', title: t('company:branding.messages.saveSuccess'), timer: 2000, showConfirmButton: false });
      },
      onError: () => {
        Dialog.alert({ icon: 'error', title: t('company:branding.messages.saveError'), message: t('company:branding.messages.saveErrorDetail') });
      },
    }
  );

  const resetMutation = useMutation(
    () => resetBranding(targetSlug),
    {
      onSuccess: () => {
        setPrimaryColor('var(--vo-celeste)');
        setSecondaryColor('var(--vo-capri)');
        setLogoFile(null);
        setLogoMiniFile(null);
        setFaviconFile(null);
        setLogoPreview(null);
        setLogoMiniPreview(null);
        setFaviconPreview(null);
        const root = document.documentElement;
        root.style.removeProperty('--color-primary');
        root.style.removeProperty('--color-secondary');
        queryClient.invalidateQueries(['company-branding', tenant?.slug]);
        Dialog.fire({ icon: 'success', title: t('company:branding.messages.resetSuccess'), timer: 2000, showConfirmButton: false });
      },
    }
  );

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleLogoMiniChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoMiniFile(file);
    setLogoMiniPreview(URL.createObjectURL(file));
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFaviconFile(file);
    setFaviconPreview(URL.createObjectURL(file));
  };

  // ── Locked state ────────────────────────────────────────────────────────────

  if (!isEligible) {
    return (
      <Container>
        <PageWrap>
          <PageHeader
            title={t('company:branding.title')}
            segments={[
              { label: t('common:breadcrumbs.manage') },
              { label: t('company:branding.title') },
            ]}
          />
          <LockedCard>
            <LockBadge><LockSvg /></LockBadge>
            <LockedTitle>{t('company:branding.locked.title')}</LockedTitle>
            <LockedDesc
              dangerouslySetInnerHTML={{ __html: t('company:branding.locked.description') }}
            />
            <FeatureChips>
              <FeatureChip><CheckSvg /><ChipText>{t('company:branding.locked.featureLogo')}</ChipText></FeatureChip>
              <FeatureChip><CheckSvg /><ChipText>{t('company:branding.locked.featureColors')}</ChipText></FeatureChip>
              <FeatureChip><CheckSvg /><ChipText>{t('company:branding.locked.featureFavicon')}</ChipText></FeatureChip>
            </FeatureChips>
            <UpgradeBtn>{t('company:branding.locked.upgradeBtn')}</UpgradeBtn>
          </LockedCard>
        </PageWrap>
      </Container>
    );
  }

  // ── Loading ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Container>
        <PageWrap>
          <PageHeader
            title={t('company:branding.title')}
            segments={[
              { label: t('common:breadcrumbs.manage') },
              { label: t('company:branding.title') },
            ]}
          />
          <ShimmerStack>
            <Shimmer style={{ height: '140px', borderRadius: '14px' }} />
            <Shimmer style={{ height: '140px', borderRadius: '14px' }} />
            <Shimmer style={{ height: '140px', borderRadius: '14px' }} />
            <Shimmer style={{ height: '180px', borderRadius: '14px' }} />
          </ShimmerStack>
        </PageWrap>
      </Container>
    );
  }

  // ── Unlocked state ──────────────────────────────────────────────────────────

  return (
    <Container>
    <PageWrap>
      <PageHeader
        title={t('company:branding.title')}
        segments={[
          { label: t('common:breadcrumbs.manage') },
          { label: t('company:branding.title') },
        ]}
      />
      <CardsCol>

        {/* Card — Logo principal */}
        <SectionCard>
          <SectionHead>
            <IconBadge><ImageSvg /></IconBadge>
            <div>
              <SectionTitle>{t('company:branding.logo.title')}</SectionTitle>
              <SectionSub>{t('company:branding.logo.subtitle')}</SectionSub>
            </div>
          </SectionHead>
          {logoPreview && (
            <NavyPreview $wide>
              <img src={logoPreview} alt="Logo preview" style={{ maxHeight: 28, maxWidth: 180, objectFit: 'contain' }} />
            </NavyPreview>
          )}
          <input ref={logoInputRef} type="file" accept="image/png,image/svg+xml" style={{ display: 'none' }} onChange={handleLogoChange} />
          <OutlineBtn onClick={() => logoInputRef.current?.click()}>
            {t('company:branding.logo.change')}
          </OutlineBtn>
        </SectionCard>

        {/* Card — Ícone / Logo mini */}
        <SectionCard>
          <SectionHead>
            <IconBadge><SquareSvg /></IconBadge>
            <div>
              <SectionTitle>{t('company:branding.logoMini.title')}</SectionTitle>
              <SectionSub>{t('company:branding.logoMini.subtitle')}</SectionSub>
            </div>
          </SectionHead>
          {logoMiniPreview && (
            <NavyPreview $square>
              <img src={logoMiniPreview} alt="Logo mini preview" style={{ maxHeight: 28, objectFit: 'contain' }} />
            </NavyPreview>
          )}
          <input ref={logoMiniInputRef} type="file" accept="image/png,image/svg+xml" style={{ display: 'none' }} onChange={handleLogoMiniChange} />
          <OutlineBtn onClick={() => logoMiniInputRef.current?.click()}>
            {t('company:branding.logoMini.change')}
          </OutlineBtn>
        </SectionCard>

        {/* Card — Favicon */}
        <SectionCard>
          <SectionHead>
            <IconBadge><MonitorSvg /></IconBadge>
            <div>
              <SectionTitle>{t('company:branding.favicon.title')}</SectionTitle>
              <SectionSub>{t('company:branding.favicon.subtitle')}</SectionSub>
            </div>
          </SectionHead>
          {faviconPreview && (
            <FaviconPreview>
              <FaviconBox>
                <img src={faviconPreview} alt="Favicon preview" style={{ width: 20, height: 20, objectFit: 'contain' }} />
              </FaviconBox>
              <FaviconName>favicon.ico</FaviconName>
            </FaviconPreview>
          )}
          <input ref={faviconInputRef} type="file" accept="image/x-icon,image/png,image/svg+xml" style={{ display: 'none' }} onChange={handleFaviconChange} />
          <OutlineBtn onClick={() => faviconInputRef.current?.click()}>
            {t('company:branding.favicon.change')}
          </OutlineBtn>
        </SectionCard>

        {/* Card — Cores da marca */}
        <SectionCard>
          <SectionHead>
            <IconBadge><PaletteSvg /></IconBadge>
            <div>
              <SectionTitle>{t('company:branding.colors.title')}</SectionTitle>
              <SectionSub>{t('company:branding.colors.subtitle')}</SectionSub>
            </div>
          </SectionHead>
          <ColorGrid>
            <ColorFieldWrap>
              <ColorLabel>{t('company:branding.colors.primaryLabel')}</ColorLabel>
              <ColorRow>
                <div style={{ position: 'relative' }}>
                  <ColorSwatch
                    $color={primaryColor}
                    onClick={() => primaryPickerRef.current?.click()}
                  />
                  <HiddenColorInput
                    ref={primaryPickerRef}
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                  />
                </div>
                <TextField
                  size="sm"
                  sx={COLOR_TEXT_SX}
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  inputProps={{ maxLength: 7 }}
                />
              </ColorRow>
            </ColorFieldWrap>
            <ColorFieldWrap>
              <ColorLabel>{t('company:branding.colors.secondaryLabel')}</ColorLabel>
              <ColorRow>
                <div style={{ position: 'relative' }}>
                  <ColorSwatch
                    $color={secondaryColor}
                    onClick={() => secondaryPickerRef.current?.click()}
                  />
                  <HiddenColorInput
                    ref={secondaryPickerRef}
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                  />
                </div>
                <TextField
                  size="sm"
                  sx={COLOR_TEXT_SX}
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  inputProps={{ maxLength: 7 }}
                />
              </ColorRow>
            </ColorFieldWrap>
          </ColorGrid>
        </SectionCard>

        {/* Action row */}
        <Divider />
        <ActionRow>
          <PrimaryBtn
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isLoading}
          >
            {t('company:branding.actions.save')}
          </PrimaryBtn>
          <SecondaryBtn
            onClick={() => resetMutation.mutate()}
            disabled={resetMutation.isLoading}
          >
            {t('company:branding.actions.reset')}
          </SecondaryBtn>
        </ActionRow>

      </CardsCol>
    </PageWrap>
    </Container>
  );
};

export default BrandingSettings;
